/**
 * Plan storage service — CRUD for Plan entities in the graph model.
 *
 * Handles the expedition planning container with decision framework,
 * timing, logistics, conditions, and pack list. Plans directly own
 * their layer groups and features.
 */

import { db } from '../database/schema.js'
import type {
  Plan,
  PlanTime,
  PlanPackItem,
  PlanEmergencyContact,
  RichText,
} from '../schemas/plan.js'
import { EntityStorageService } from './entity-storage.js'
import { validatePlan, runValidation } from './validation.js'
import type { StorageResult } from './base-storage.js'
import { edgeService, nodeRef } from '../services/edge-service.js'
import type { Origin } from '../schemas/origin.js'

export interface CreatePlanData {
  name: string
  description?: string | undefined
  descriptionRich?: RichText | undefined
  location?: Plan['location'] | undefined
  // Times
  times?: PlanTime[] | undefined
  // Decision Framework
  decisionFactors?: Plan['decisionFactors'] | undefined
  decisionRules?: string[] | undefined
  objectiveHazards?: string | undefined
  objectiveHazardsRich?: RichText | undefined
  // Conditions
  weatherForecast?: string | undefined
  weatherCurrent?: string | undefined
  avyForecast?: string | undefined
  // Emergency contacts
  emergencyContacts?: PlanEmergencyContact[] | undefined
  // Pack list
  teamGear?: PlanPackItem[] | undefined
  individualGear?: PlanPackItem[] | undefined
  // Provenance
  createdFrom?: { type: 'route' | 'activity'; id: string } | undefined
  origin: Origin
  createdBy: string
}

class PlanStorageService extends EntityStorageService<Plan> {
  protected readonly nodeType = 'plan'

  constructor() {
    super(db.plans, 'plan')
  }

  async createPlan(data: CreatePlanData): Promise<StorageResult<Plan>> {
    const planData = {
      name: data.name,
      description: data.description,
      descriptionRich: data.descriptionRich,
      location: data.location,
      // Times
      times: data.times ?? [],
      // Decision Framework
      decisionFactors: data.decisionFactors ?? [],
      decisionRules: data.decisionRules ?? [],
      objectiveHazards: data.objectiveHazards,
      objectiveHazardsRich: data.objectiveHazardsRich,
      // Conditions
      weatherForecast: data.weatherForecast,
      weatherCurrent: data.weatherCurrent,
      avyForecast: data.avyForecast,
      // Emergency contacts
      emergencyContacts: data.emergencyContacts ?? [],
      // Pack list
      teamGear: data.teamGear ?? [],
      individualGear: data.individualGear ?? [],
      // Provenance
      createdFrom: data.createdFrom,
      origin: data.origin,
      createdBy: data.createdBy,
    }

    const validation = runValidation(validatePlan, planData, { context: 'create' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }

    const result = await this.createWithLayerGroup(
      planData as Omit<Plan, 'id' | 'createdAt' | 'updatedAt'>
    )

    if (result.success && result.data && data.createdBy) {
      await this.addMember(result.data.id, data.createdBy, 'leader', 'active')
    }

    return result
  }

  async updatePlan(id: string, updates: Partial<Plan>): Promise<StorageResult<Plan>> {
    const validation = runValidation(validatePlan, updates, { context: 'update' })
    if (!validation.isValid) {
      return this.handleValidationError(validation)
    }
    return this.update(id, updates)
  }

  async deletePlan(id: string): Promise<StorageResult<void>> {
    return this.deleteWithEdges(id)
  }

  async getPlansForUser(_userId: string): Promise<StorageResult<Plan[]>> {
    return this.executeOperation(async () => {
      const all = await this.table.toArray()
      return all.filter(p => (p as Record<string, unknown>).accessStatus !== 'revoked')
    }, 'get plans for user')
  }

  /** Add a team member via has-member edge */
  async addMember(
    planId: string,
    userId: string,
    role: string = 'member',
    status: string = 'invited'
  ): Promise<void> {
    const exists = await edgeService.exists(
      nodeRef('plan', planId),
      'has-member',
      nodeRef('user', userId)
    )
    if (!exists) {
      await edgeService.create({
        subject: nodeRef('plan', planId),
        predicate: 'has-member',
        object: nodeRef('user', userId),
        properties: { role, status, joinedAt: new Date().toISOString() },
      })
    }
  }

  /** Get member user IDs for this plan */
  async getMemberIds(planId: string): Promise<string[]> {
    return edgeService.getObjectIds(nodeRef('plan', planId), 'has-member')
  }

  /** Get activity IDs linked to this plan */
  async getActivityIds(planId: string): Promise<string[]> {
    return edgeService.getObjectIds(nodeRef('plan', planId), 'has-activity')
  }

  /** Update shared contact info on a plan membership edge */
  async updateMemberContact(
    planId: string,
    userId: string,
    sharedContact: {
      phone?: string
      email?: string
      emergencyContact?: { name: string; phone: string; relationship: string }
    }
  ): Promise<void> {
    const edges = await edgeService.getBySubject(nodeRef('plan', planId), 'has-member')
    const edge = edges.find(e => e.object === nodeRef('user', userId))
    if (!edge) return

    const properties = { ...(edge.properties ?? {}), sharedContact }
    await edgeService.updateProperties(edge.id, properties)
  }

  /** Remove a member from a plan */
  async removeMember(planId: string, userId: string): Promise<void> {
    const edges = await edgeService.getBySubject(nodeRef('plan', planId), 'has-member')
    const edge = edges.find(e => e.object === nodeRef('user', userId))
    if (edge) {
      await edgeService.delete(edge.id)
    }
  }

  /** Update a member's role */
  async updateMemberRole(planId: string, userId: string, newRole: string): Promise<void> {
    const edges = await edgeService.getBySubject(nodeRef('plan', planId), 'has-member')
    const edge = edges.find(e => e.object === nodeRef('user', userId))
    if (!edge) return

    const properties = { ...(edge.properties ?? {}), role: newRole }
    await edgeService.updateProperties(edge.id, properties)
  }

  /** Count leaders for a plan */
  async getLeaderCount(planId: string): Promise<number> {
    const edges = await edgeService.getBySubject(nodeRef('plan', planId), 'has-member')
    return edges.filter(e => {
      const props = (e.properties ?? {}) as Record<string, unknown>
      return props.role === 'leader'
    }).length
  }
}

export const planStorage = new PlanStorageService()
